import { useState, useEffect } from 'react';
import { Accessibility, Plus, Minus, X } from 'lucide-react';

const FILTERS = [
  { id: 'bigger', label: 'Increase Text', icon: <Plus className="w-4 h-4" /> },
  { id: 'smaller', label: 'Decrease Text', icon: <Minus className="w-4 h-4" /> },
  { id: 'grayscale', label: 'Grayscale' },
  { id: 'highContrast', label: 'High Contrast' },
  { id: 'negativeContrast', label: 'Negative Contrast' },
  { id: 'lightBg', label: 'Light Background' },
  { id: 'underlineLinks', label: 'Links Underline' },
  { id: 'readableFont', label: 'Readable Font' }
];

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState({});
  const [fontSize, setFontSize] = useState(100);

  useEffect(() => {
    const el = document.getElementById('main-wrapper');
    if (!el) return;
    const classes = [];
    if (active.grayscale) classes.push('a11y-grayscale');
    if (active.highContrast) classes.push('a11y-high-contrast');
    if (active.negativeContrast) classes.push('a11y-negative');
    if (active.lightBg) classes.push('a11y-light-bg');
    if (active.underlineLinks) classes.push('a11y-underline-links');
    if (active.readableFont) classes.push('a11y-readable-font');
    el.className = el.className.replace(/a11y-\S+/g, '').trim();
    if (classes.length) el.className += ' ' + classes.join(' ');
    // Apply font size to <html> root so ALL rem-based Tailwind sizes scale
    document.documentElement.style.fontSize = fontSize + '%';
  }, [active, fontSize]);

  const toggle = (id) => {
    if (id === 'bigger') { setFontSize(prev => Math.min(prev + 10, 150)); return; }
    if (id === 'smaller') { setFontSize(prev => Math.max(prev - 10, 80)); return; }
    setActive(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const reset = () => { setActive({}); setFontSize(100); document.documentElement.style.fontSize = ''; };
  const hasActive = Object.values(active).some(Boolean) || fontSize !== 100;

  return (
    <div className="fixed left-0 z-[60]" style={{ top: '80px' }} data-testid="accessibility-widget">
      <button onClick={() => setOpen(!open)} className={`w-10 h-10 rounded-r-lg flex items-center justify-center shadow-lg transition-colors ${hasActive ? 'bg-cwc-gold text-cwc-navy' : 'bg-cwc-navy text-white'}`} aria-label="Accessibility options" data-testid="accessibility-toggle">
        <Accessibility className="w-5 h-5" />
      </button>
      {open && (
        <div className="absolute left-0 top-12 bg-white rounded-r-xl shadow-2xl w-56 p-4 space-y-2" data-testid="accessibility-panel">
          <div className="flex items-center justify-between mb-2">
            <p className="font-semibold text-sm text-cwc-navy">Accessibility</p>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
          </div>
          {FILTERS.map(f => (
            <button key={f.id} onClick={() => toggle(f.id)} className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors ${active[f.id] ? 'bg-cwc-gold/20 text-cwc-navy font-medium' : 'hover:bg-gray-50 text-gray-700'}`} data-testid={`a11y-${f.id}`}>
              {f.icon || <span className="w-4 h-4 rounded border border-gray-300 inline-block" />}
              {f.label}
            </button>
          ))}
          {hasActive && (
            <button onClick={reset} className="w-full text-center text-xs text-red-500 hover:text-red-700 mt-2 py-1" data-testid="a11y-reset">Reset All</button>
          )}
        </div>
      )}
    </div>
  );
}
